import { Trans, useTranslation } from 'react-i18next'
import i18n from '../i18n'
import { formatNorwegianDate } from '../utils/dateUtils'

interface PeriodInfo {
  label: string
  start_date: string
  end_date: string
}

interface PeriodsInfo {
  current: PeriodInfo
  previous: PeriodInfo
  ytd_current?: PeriodInfo | null
  ytd_previous?: PeriodInfo | null
}

export interface QueryExplanationProps {
  /** Full periods object from API (use for pages that return periods from backend) */
  periods?: PeriodsInfo | null
  /** Latest SVV data date (YYYY-MM-DD) */
  maxDataDate: string | null
  /** Active output mode (year_to_date, month_to_date, etc.) */
  outputMode: string
  /** Vehicle type filter. null leaves the personbil/varebil sentence out, for
   *  pages about other vehicle classes (Andre kjøretøy, #982). */
  vehicleFilter: 'personbil' | 'varebil' | 'alle' | null
  /** Active postnummer filter value, or null */
  postnummerFilter?: string | null
  /**
   * Human name for what the postnummer filter represents — normally the active
   * markedsområde (#527). When set it replaces the postnummer list entirely: an
   * område can hold hundreds of codes, and printing them all buries the rest of
   * the explanation. The count is still shown, so the scope stays visible.
   */
  postnummerLabel?: string | null
  /** Active tab (for pages with tabs like loan/leasing/generelt) */
  activeTab?: string
  /** Drill-down context label, e.g. "varianter for Toyota Corolla" */
  drillDownLabel?: string | null
  /**
   * Page-specific filter sentences, shown after the vehicle type (#1014).
   * Bruktbil passes its geo sentence here too, so it omits postnummerFilter.
   */
  filterLines?: string[]
  /** Page-specific extra warning lines */
  extraWarnings?: string[]
  /** Custom label for max data date (default: "Siste tilgjengelige registreringsdata") */
  maxDataDateLabel?: string
  /** Simple period label, e.g. "Hittil i år" (used when periods is not available) */
  periodLabel?: string
  /** Simple start date YYYY-MM-DD (used when periods is not available) */
  startDate?: string
  /** Simple end date YYYY-MM-DD (used when periods is not available) */
  endDate?: string
}

/** How many raw postnumre to spell out before summarising to a count (#527). */
const MAX_LISTED_POSTNUMMER = 8

/** "3 postnumre" / "1 postnummer" for a comma-separated list. */
export function postnummerCount(postnummerFilter: string): string {
  const n = postnummerFilter.split(',').map(c => c.trim()).filter(Boolean).length
  return n === 1 ? i18n.t('components:queryExplanation.postnummerOne') : i18n.t('components:queryExplanation.postnummerMany', { n })
}

/**
 * One sentence describing the postnummer filter (#527).
 *
 * Prefers the markedsområde name when there is one. Otherwise lists the codes,
 * but stops at MAX_LISTED_POSTNUMMER — a saved område can hold hundreds, and
 * the old behaviour printed every single one, pushing everything else in the
 * box off the screen.
 */
export function describePostnummerFilter(
  postnummerFilter?: string | null,
  postnummerLabel?: string | null,
): string | null {
  if (!postnummerFilter) return null
  const codes = postnummerFilter.split(',').map(c => c.trim()).filter(Boolean)
  const antall = codes.length
  const suffix = antall === 1 ? i18n.t('components:queryExplanation.postnummerOne') : i18n.t('components:queryExplanation.postnummerMany', { n: antall })

  if (postnummerLabel) {
    return i18n.t('components:queryExplanation.filteredByArea', { label: postnummerLabel, amount: suffix })
  }
  if (antall > MAX_LISTED_POSTNUMMER) {
    const shown = codes.slice(0, MAX_LISTED_POSTNUMMER).join(', ')
    return i18n.t('components:queryExplanation.filteredByCountList', { amount: suffix, codes: shown, more: antall - MAX_LISTED_POSTNUMMER })
  }
  return i18n.t('components:queryExplanation.filteredByList', { codes: codes.join(', ') })
}

/**
 * Build a plain-text explanation string from query parameters.
 * Reusable for both the UI component and PDF export metadata.
 */
export function buildExplanationText(props: QueryExplanationProps): string {
  const { periods, maxDataDate, outputMode, vehicleFilter, postnummerFilter, postnummerLabel, drillDownLabel, filterLines, extraWarnings, maxDataDateLabel, periodLabel, startDate, endDate } = props
  const parts: string[] = []

  // Drill-down context
  if (drillDownLabel) {
    parts.push(i18n.t('components:queryExplanation.showing', { label: drillDownLabel }))
  }

  // Period description — full mode (periods object) or simple mode (label + dates)
  if (periods) {
    const currentEnd = getDisplayEndDate(periods.current.end_date, maxDataDate, outputMode)
    const previousEnd = getDisplayPreviousEndDate(periods.previous.end_date, maxDataDate, outputMode)
    parts.push(
      i18n.t('components:queryExplanation.periodsCompared', {
        current: periods.current.label,
        currentStart: formatNorwegianDate(periods.current.start_date),
        currentEnd: formatNorwegianDate(currentEnd),
        previous: periods.previous.label,
        previousStart: formatNorwegianDate(periods.previous.start_date),
        previousEnd: formatNorwegianDate(previousEnd),
      })
    )
  } else if (startDate && endDate) {
    const displayEnd = getDisplayEndDate(endDate, maxDataDate, outputMode)
    parts.push(i18n.t('components:queryExplanation.periodSimple', {
      label: periodLabel || i18n.t('components:queryExplanation.selectedPeriod'),
      start: formatNorwegianDate(startDate),
      end: formatNorwegianDate(displayEnd),
    }))
  }

  // Vehicle type
  if (vehicleFilter === 'personbil') {
    parts.push(i18n.t('components:queryExplanation.personbilOnly'))
  } else if (vehicleFilter === 'varebil') {
    parts.push(i18n.t('components:queryExplanation.varebilOnly'))
  } else if (vehicleFilter === 'alle') {
    parts.push(i18n.t('components:queryExplanation.allVehicles'))
  }

  if (filterLines && filterLines.length > 0) {
    parts.push(...filterLines)
  }

  // Postnummer
  const postnummerText = describePostnummerFilter(postnummerFilter, postnummerLabel)
  if (postnummerText) {
    parts.push(postnummerText)
  }

  // Max data date
  if (maxDataDate) {
    parts.push(i18n.t('components:queryExplanation.maxDataDate', {
      label: maxDataDateLabel || i18n.t('components:queryExplanation.maxDataDateLabel'),
      date: formatNorwegianDate(maxDataDate),
    }))
  }

  // Extra warnings
  if (extraWarnings && extraWarnings.length > 0) {
    parts.push(...extraWarnings)
  }

  return parts.join(' ')
}

/** Cap end date to maxDataDate for YTD/MTD modes */
function getDisplayEndDate(backendEndDate: string, maxDataDate: string | null, outputMode: string): string {
  if (maxDataDate && (outputMode === 'year_to_date' || outputMode === 'month_to_date')) {
    return maxDataDate
  }
  return backendEndDate
}

/** Cap previous-period end date (same month-day, year - 1) */
function getDisplayPreviousEndDate(backendEndDate: string, maxDataDate: string | null, outputMode: string): string {
  if (maxDataDate && (outputMode === 'year_to_date' || outputMode === 'month_to_date')) {
    const year = parseInt(maxDataDate.substring(0, 4)) - 1
    return `${year}${maxDataDate.substring(4)}`
  }
  return backendEndDate
}

/**
 * Blue explanation box displayed above the grid.
 * Describes the active query parameters in human-readable Norwegian text.
 * Styled to match PeriodInfoBanner (replaces it when used).
 */
export default function QueryExplanationBox(props: QueryExplanationProps) {
  const { periods, maxDataDate, outputMode, vehicleFilter, postnummerFilter, postnummerLabel, drillDownLabel, filterLines, extraWarnings, periodLabel, startDate, endDate } = props
  const { t } = useTranslation('components')
  const b = { b: <strong /> }

  // Don't render until we have period data or simple date range
  if (!periods && !startDate) return null

  const currentEnd = periods ? getDisplayEndDate(periods.current.end_date, maxDataDate, outputMode) : undefined
  const previousEnd = periods ? getDisplayPreviousEndDate(periods.previous.end_date, maxDataDate, outputMode) : undefined
  const simpleEnd = (!periods && endDate) ? getDisplayEndDate(endDate, maxDataDate, outputMode) : undefined

  return (
    <div className="card p-4 bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-800">
      <p className="text-sm text-blue-800 dark:text-blue-300 leading-relaxed">
        {/* Drill-down context */}
        {drillDownLabel && (
          <><Trans t={t} i18nKey="queryExplanation.showingRich" values={{ label: drillDownLabel }} components={b} />{' '}</>
        )}

        {/* Period — full mode */}
        {periods && currentEnd && previousEnd && (
          <>
            <Trans
              t={t}
              i18nKey="queryExplanation.periodsComparedRich"
              values={{
                current: periods.current.label,
                currentStart: formatNorwegianDate(periods.current.start_date),
                currentEnd: formatNorwegianDate(currentEnd!),
                previous: periods.previous.label,
                previousStart: formatNorwegianDate(periods.previous.start_date),
                previousEnd: formatNorwegianDate(previousEnd!),
              }}
              components={b}
            />
            {' '}
          </>
        )}

        {/* Period — simple mode */}
        {!periods && startDate && endDate && (
          <>
            <Trans
              t={t}
              i18nKey="queryExplanation.periodSimpleRich"
              values={{
                label: periodLabel || t('queryExplanation.selectedPeriod'),
                start: formatNorwegianDate(startDate!),
                end: simpleEnd ? formatNorwegianDate(simpleEnd) : '',
              }}
              components={b}
            />
            {' '}
          </>
        )}

        {/* Vehicle type */}
        {vehicleFilter === 'personbil' && <Trans t={t} i18nKey="queryExplanation.personbilOnlyRich" components={b} />}
        {vehicleFilter === 'varebil' && <Trans t={t} i18nKey="queryExplanation.varebilOnlyRich" components={b} />}
        {vehicleFilter === 'alle' && <Trans t={t} i18nKey="queryExplanation.allVehiclesRich" components={b} />}

        {filterLines && filterLines.length > 0 && (
          <>{' '}<span data-testid="query-explanation-filters">{filterLines.join(' ')}</span></>
        )}

        {/* Postnummer — name of the markedsområde when known, never the full
            list of codes (#527). */}
        {postnummerFilter && (
          <>{' '}<span data-testid="query-explanation-postnummer">
            {postnummerLabel ? (
              <Trans
                t={t}
                i18nKey="queryExplanation.filteredByAreaRich"
                values={{ label: postnummerLabel, amount: postnummerCount(postnummerFilter) }}
                components={b}
              />
            ) : (
              describePostnummerFilter(postnummerFilter, null)
            )}
          </span></>
        )}

        {/* Max data date */}
        {maxDataDate && (
          <>{' '}<span className="italic text-blue-500 dark:text-blue-400">{t('queryExplanation.maxDataDate', { label: props.maxDataDateLabel || t('queryExplanation.maxDataDateLabel'), date: formatNorwegianDate(maxDataDate!) })}</span></>
        )}

        {/* Extra warnings */}
        {extraWarnings && extraWarnings.map((warning, i) => (
          <span key={i}>{' '}{warning}</span>
        ))}
      </p>
    </div>
  )
}
